import { useCallback } from 'react';
import { type EditorView } from '@tiptap/pm/view';

import {
  prepareEquationHTML,
  prepareTiptapHTML,
} from '@/shared/features/rich-text';
import { extractBareText } from '@/shared/utils/extractBareText';

import {
  getOrderItemTextLength,
  ORDER_ITEM_TEXT_MAX_LENGTH,
} from '../../../form/schema/quiz/QuizOrderDataSchema';
import {
  goToNextFocusable,
  goToPreviousFocusable,
} from '../../focus/focusChain';
import {
  isNextFocusKeyboardEvent,
  isPreviousFocusKeyboardEvent,
} from '../../focus/focusUtils';
import { useBuildTextEditor } from '../hook/useBuildTextEditor';
import { useQuizFormField } from '../hook/useQuizFormField';
import { useQuizControl } from '../QuizControlProvider';

/**
 * 보기 1행의 TipTap 에디터.
 *
 * `useMultipleOptionTextEditor` 와 달리 **마지막 보기에서 Enter 를 눌러도
 * 새 문제를 만들지 않는다.** 순서 맞추기는 배열 순서가 정답이라, 의도치 않은
 * 문제 생성보다 현재 문항의 보기 편집을 끝까지 마치는 흐름이 자연스럽다.
 * 포커스 체인 이동(Tab/Shift+Tab)만 지원한다.
 *
 * 초기 내용은 `richText` → `text` → `equation` 순으로 해석한다.
 * `equation` 은 서버·AI 가 수식만 담아 보낼 수 있는 별도 필드라, 이걸 빠뜨리면
 * 수식만 있는 보기가 편집 화면에서 **빈칸으로 보이고 저장 시 사라진다**
 * (MATCH 의 `EditableMatch.tsx:295-315` 와 같은 규칙).
 */
export const useOrderItemTextEditor = ({
  itemIndex,
}: {
  itemIndex: number;
}) => {
  const { index: quizIndex } = useQuizControl();

  const [text, updateText] = useQuizFormField({
    field: `orderData.items.${itemIndex}.text`,
  });
  const [richText, updateRichText] = useQuizFormField({
    field: `orderData.items.${itemIndex}.richText`,
  });
  const [equation, updateEquation] = useQuizFormField({
    field: `orderData.items.${itemIndex}.equation`,
  });

  const { editor } = useBuildTextEditor({
    characterLimit: ORDER_ITEM_TEXT_MAX_LENGTH,
    characterCounter: getOrderItemTextLength,
    content:
      richText ||
      (text
        ? prepareTiptapHTML(text)
        : equation
        ? prepareEquationHTML(equation)
        : ''),
    onUpdate: (content: string) => {
      updateText(extractBareText(content));
      updateRichText(content);
      // 편집을 시작한 순간부터 수식은 richText 안의 latex 노드로 산다.
      // `equation` 을 남겨두면 다음 로드 때 옛 수식이 되살아나 본문을 덮어쓴다.
      updateEquation(undefined);
    },
  });

  const fieldPath = `quizList.${quizIndex}.orderData.items.${itemIndex}.text`;

  const handleKeyDown = useCallback(
    (_: EditorView, event: KeyboardEvent) => {
      if (isNextFocusKeyboardEvent(event)) {
        event.preventDefault();
        goToNextFocusable({ current: fieldPath });
        return true;
      }
      if (isPreviousFocusKeyboardEvent(event)) {
        event.preventDefault();
        goToPreviousFocusable({ current: fieldPath });
        return true;
      }
    },
    [fieldPath],
  );

  editor?.setOptions({
    editorProps: { handleKeyDown },
  });

  return { editor };
};
